import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth as authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Mail, Lock, User, AlertCircle, ArrowLeft, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    name: '',
    pseudo: ''
  });

  // Password reset states
  const [resetMode, setResetMode] = useState(false); // show reset flow
  const [resetStep, setResetStep] = useState(1); // 1=email, 2=code, 3=new password
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleRequestCode = async () => {
    if (!resetEmail) { setError('Entrez votre adresse email'); return; }
    setResetLoading(true);
    setError('');
    try {
      await authApi.requestCode(resetEmail);
      setResetStep(2);
      toast.success(`Code envoyé à ${resetEmail}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi du code");
    } finally { setResetLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (!resetCode || resetCode.length !== 6) { setError('Entrez le code à 6 chiffres'); return; }
    setResetLoading(true);
    setError('');
    try {
      await authApi.verifyCode(resetEmail, resetCode);
      setResetStep(3);
      toast.success('Code vérifié ! Choisissez un nouveau mot de passe.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Code incorrect');
    } finally { setResetLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { setError('Le mot de passe doit faire au moins 6 caractères'); return; }
    if (newPassword !== confirmNewPassword) { setError('Les mots de passe ne correspondent pas'); return; }
    setResetLoading(true);
    setError('');
    try {
      await authApi.resetPassword(resetEmail, resetCode, newPassword);
      toast.success('Mot de passe réinitialisé ! Connectez-vous.');
      setResetMode(false);
      setResetStep(1);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setLoginData({ ...loginData, email: resetEmail });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally { setResetLoading(false); }
  };

  const exitResetMode = () => {
    setResetMode(false);
    setResetStep(1);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(loginData.email, loginData.password);
      toast.success(`Bienvenue, ${user.name}!`);
      navigate('/discover');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!registerData.pseudo || registerData.pseudo.replace(/\s/g, '').length < 2) {
      setError('Le pseudo doit contenir au moins 2 caractères');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      const user = await register({
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        pseudo: registerData.pseudo.replace(/\s/g, ''),
      });
      toast.success(`Bienvenue, ${user.name}!`);
      navigate('/discover');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur d\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center glow-primary">
              <Trophy className="w-8 h-8 text-primary-foreground" />
            </div>
            <span className="text-3xl font-bold tracking-tight">
              <span className="text-primary">Arcan</span>app
            </span>
          </Link>
          <p className="text-muted-foreground mt-2">Trouvez des tournois TCG près de chez vous</p>
        </div>

        <Card className="border-border bg-card">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Inscription</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <CardHeader>
                <CardTitle>{resetMode ? 'Réinitialiser le mot de passe' : 'Connexion'}</CardTitle>
                <CardDescription>
                  {resetMode 
                    ? resetStep === 1 ? 'Entrez votre email pour recevoir un code de réinitialisation'
                    : resetStep === 2 ? 'Entrez le code à 6 chiffres reçu par email'
                    : 'Choisissez votre nouveau mot de passe'
                    : 'Entrez vos identifiants pour accéder à votre compte'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {resetMode ? (
                  <div className="space-y-4">
                    {error && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}

                    {resetStep === 1 && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="reset-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="reset-email"
                              type="email"
                              placeholder="email@exemple.com"
                              className="pl-10"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              required
                              data-testid="reset-email-input"
                            />
                          </div>
                        </div>
                        <Button className="w-full" disabled={resetLoading} onClick={handleRequestCode} data-testid="reset-send-code-btn">
                          {resetLoading ? 'Envoi...' : 'Envoyer le code'}
                        </Button>
                      </>
                    )}

                    {resetStep === 2 && (
                      <>
                        <p className="text-sm text-muted-foreground">Code envoyé à <strong>{resetEmail}</strong></p>
                        <div className="space-y-2">
                          <Label htmlFor="reset-code">Code à 6 chiffres</Label>
                          <div className="relative">
                            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="reset-code"
                              type="text"
                              placeholder="123456"
                              className="pl-10 tracking-widest text-center font-mono"
                              maxLength={6}
                              value={resetCode}
                              onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                              required
                              data-testid="reset-code-input"
                            />
                          </div>
                        </div>
                        <Button className="w-full" disabled={resetLoading} onClick={handleVerifyCode} data-testid="reset-verify-code-btn">
                          {resetLoading ? 'Vérification...' : 'Vérifier le code'}
                        </Button>
                        <button
                          type="button"
                          className="w-full text-sm text-primary hover:underline"
                          onClick={handleRequestCode}
                          disabled={resetLoading}
                          data-testid="reset-resend-code-btn"
                        >
                          Renvoyer un nouveau code
                        </button>
                      </>
                    )}

                    {resetStep === 3 && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="new-password">Nouveau mot de passe</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="new-password"
                              type="password"
                              placeholder="••••••••"
                              className="pl-10"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              required
                              data-testid="reset-new-password-input"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirm-new-password">Confirmer le mot de passe</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="confirm-new-password"
                              type="password"
                              placeholder="••••••••"
                              className="pl-10"
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              required
                              data-testid="reset-confirm-password-input"
                            />
                          </div>
                        </div>
                        <Button className="w-full" disabled={resetLoading} onClick={handleResetPassword} data-testid="reset-submit-btn">
                          {resetLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                        </Button>
                      </>
                    )}

                    <button
                      type="button"
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto mt-2"
                      onClick={exitResetMode}
                      data-testid="reset-back-to-login-btn"
                    >
                      <ArrowLeft className="w-3 h-3" />
                      Retour à la connexion
                    </button>
                  </div>
                ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="email@exemple.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                    {loading ? 'Connexion...' : 'Se connecter'}
                  </Button>

                  <button
                    type="button"
                    className="w-full text-sm text-primary hover:underline"
                    onClick={() => { setResetMode(true); setError(''); setResetEmail(loginData.email); }}
                    data-testid="forgot-password-btn"
                  >
                    Mot de passe oublié ?
                  </button>
                </form>
                )}
              </CardContent>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <CardHeader>
                <CardTitle>Inscription</CardTitle>
                <CardDescription>Créez votre compte pour rejoindre la communauté</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nom</Label>
                      <Input
                        id="register-name"
                        placeholder="Votre nom"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-pseudo">Pseudo *</Label>
                      <Input
                        id="register-pseudo"
                        placeholder="Votre pseudo"
                        value={registerData.pseudo}
                        onChange={(e) => setRegisterData({ ...registerData, pseudo: e.target.value.replace(/\s/g, '') })}
                        required
                        data-testid="register-pseudo-input"
                      />
                      <p className="text-xs text-muted-foreground">Visible par les autres joueurs</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="email@exemple.com"
                        className="pl-10"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirmer le mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        required
                        data-testid="register-confirm-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                    {loading ? 'Inscription...' : 'S\'inscrire'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
